import { Box, ButtonBase, useTheme } from "@mui/material";
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import Chart from "react-apexcharts";
import { chartTokens, tokens } from "../../../theme";
import ShChart from "./ShChart";
import ChartCard from "../cards/ChartCard";
import ZoomInMapOutlinedIcon from '@mui/icons-material/ZoomInMapOutlined';
import ZoomOutMapOutlinedIcon from '@mui/icons-material/ZoomOutMapOutlined';
import HomeIcon from '@mui/icons-material/Home';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import SearchIcon from '@mui/icons-material/Search';

import cx from 'classnames';
import { NoChartData } from "./Status/NoChartData";
import { ChartError } from "./Status/ChartError";
import { useTranslation } from "react-i18next";
import Text from "../text/Text";
import { ChartLegendPopover } from "../../global/StockhubBI/charts/components/ChartLegendPopover";

// Single static dataset for builder previews. A preview only renders a tinted placeholder, so
// there's no point recomputing/cloning a series per render — every preview reuses this.
const PREVIEW_SERIES = [{ name: "Preview", data: [40, 55, 48, 62, 70, 58, 65] }];

const AreaContent = (props) => {

    return (
        <Chart

            className="sh-area-chart"
            // key={props.key}
            // ref={props.ref}
            type="area"
            series={props.series}
            options={props.options}
            height={props.height}
        />
    )
}

export const AreaChartContentReports = (props) => {
    if(typeof props.colors === "undefined" && typeof props.staticColors === "undefined") throw new Error("Colors not defined");
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const chartRef = useRef(null);
    const {i18n} = useTranslation();
    const containerRef = useRef(null);
    const [chartMode, setChartMode] = useState('zoom'); 
    const colorType = theme.palette.mode === "dark" ? "light" : "st"; 
    const {t} = useTranslation();

    const getColor = () => {
        if(typeof props.color === "undefined") return colors.txt["secondary"];
        if(props.color !== "default") return chartTokens.text.primary;
        return colors.txt["secondary"];
    }

    const getButtonTextColor = () => {
        if(props.color === "default" && theme.palette.mode === "dark") {
            return colors.txt.primary;
        }
        return chartTokens.text.primary;
    }

    const randomData = Array.from({ length: 7 }, () => Math.floor(Math.random() * 60) + 40);
   
    // Define the initial values based on the isLoading prop
    const values = props.isLoading || props.isError ? [{
        name: 'Loading',
        data: randomData
    }] : props.values

    const allValuesAreZero = useMemo(() => {
        return values.every(item => item.data.every(value => value === 0 || isNaN(value)));
    }, [values]);

    // Deep clone series to prevent ApexCharts from mutating frozen objects (e.g., from Redux/RTK Query)
    const series = useMemo(() => {
        // Builder preview only shows a tinted placeholder — reuse the one static dataset instead of
        // recomputing/cloning the real series each render.
        if (props.preview) return PREVIEW_SERIES;
        const rawSeries = !allValuesAreZero
            ? (props.useTranslation
                ? values.map((value) => ({
                    name: t(value.name.toLowerCase()),
                    data: value.data
                }))
                : values)
            : [{name: "Data", data: randomData}];
        return JSON.parse(JSON.stringify(rawSeries));
    }, [props.preview, allValuesAreZero, values, props.useTranslation, t, randomData]);

    const getChartColors = () => {
        if (props.isLoading || allValuesAreZero) {
            // A dashboard-builder preview tints the placeholder in the flat preview color; otherwise plain grey.
            return [props.preview ? colors.purple[400] : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100])];
        }
        if(props.isError) {
            return [colors.red[200]]
        }

        //Static colors take priority
        if(typeof props.staticColors !== "undefined") return props.staticColors;


        let myColors = [];

        var keys = Object.keys(chartTokens[props.colors[0]][colorType]);
        for (var i = keys.length - 1; i >= keys.length - values.length; i--) {
            myColors.push(chartTokens[props.colors[0]][colorType][keys[i]]);
        }
        return myColors;

    }

    // Memoize tooltip formatter to prevent recreation on every render
    const tooltipFormatter = useCallback(({ series, dataPointIndex, w }) => {
        // Get the actual category from props.categories instead of w.globals.labels
        let category = props.categories?.[dataPointIndex] ?? dataPointIndex;

        // Apply xaxis formatter if it exists
        if (props.chartSettings?.chart?.labelFormat?.xaxis) {
            category = props.chartSettings.chart.labelFormat.xaxis(i18n, category);
        }

        const formatterFn = props.chartSettings?.chart?.labelFormat?.dataLabels;

        let tooltipHtml = `
            <div class="apex-tooltip-custom" style="padding:8px 12px;">
                <div style="margin-bottom:6px;font-weight:600;">
                ${category}
                </div>
        `;

        w.globals.seriesNames.forEach((name, i) => {
            let val = series[i][dataPointIndex];

            // Hide exact zero
            if (val === 0) val = 0;

            // Hide values that would round to 0 (e.g. 0.001 → €0.00)
            if (Math.round(val * 100) / 100 === 0) val = 0;

            if (typeof formatterFn === 'function') {
                val = formatterFn(i18n, val);
            }

            if (val !== undefined) {
                tooltipHtml += `
                    <div style="display:flex;align-items:center;margin:2px 0;">
                        <span
                            style="
                                width:10px;
                                height:10px;
                                background:${w.globals.colors[i]};
                                display:inline-block;
                                margin-right:6px;
                                border-radius:5px;
                            "
                        >
                        </span>
                        <span>${t(name)}: <b>${val}</b></span>
                    </div>
                `;
            }
        });

        tooltipHtml += `</div>`;
        return tooltipHtml;
    }, [props.chartSettings?.chart?.labelFormat?.dataLabels, props.chartSettings?.chart?.labelFormat?.xaxis, props.categories, i18n, t]);
    const labelColor = useMemo(() => {
        return theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"];
    }, [theme.palette.mode, colors.txt]);

    const options = {
        chart: {
            selection: {
              enabled: true
            },
            pan: {
                enabled:true,
            },
            stacked: props.stacked ?? true,
            toolbar: {
                autoSelected: chartMode,
                tools: {
                  download: true,
                  selection: true,
                  zoom: true,
                  zoomin: true,
                  zoomout: true,
                  pan: true
                }
            },
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            colors: undefined,
            width:0
        },
        tooltip: {
            shared: window.innerWidth >= 768,
            intersect: window.innerWidth < 768,
            followCursor: true,
            custom: tooltipFormatter
        },
        colors:getChartColors(),
        fill: {
            type: 'solid',
            // gradient: {
            //     // shade: "light",
            //     type: "vertical",
            //     shadeIntensity: 0,
            //     opacityFrom: 1,
            //     opacityTo: 1 
            // }
        },
        legend:{
            show:false,
        },
        grid:{
            show:false,
        },
        xaxis: {
            categories: props.categories ?? [1991, 1992, 1993, 1994, 1995, 1996, 1997, 1998, 1999],
            tickAmount: props.tickAmount ?? 5,
            labels: {
                // noSlant keeps x-axis labels horizontal (hides overlapping ones instead of rotating them).
                ...(props.noSlant ? { rotate: 0, rotateAlways: false, hideOverlappingLabels: true } : {}),
                style: {
                    colors:labelColor,
                    // colors: ["blue","orange","grey"],
                    fontSize: '12px',
                    // fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 500,

                    cssClass: 'apexcharts-xaxis-label',
                },
                offsetX: 0,
                formatter: function (val) {
                    if (props.chartSettings?.chart?.labelFormat?.xaxis) {
                        return props.chartSettings.chart.labelFormat.xaxis(i18n, val);
                    }

                    return val;
                }
            },
            axisBorder: {
                show: false,
            },
        },
        yaxis: {
            min: props.min ?? 0,  // Set the minimum value for the Y-axis
            show:true,
            // opposite: true
            // labels: {
            //     style: {
            //         colors:getColor(),
            //         // colors: ["blue","orange","grey"],
            //         fontSize: '12px',
            //         // fontFamily: 'Helvetica, Arial, sans-serif',
            //         fontWeight: 500,
            //         // cssClass: 'apexcharts-xaxis-label',
            //     },
            //     offsetX: 0,
            //     offsetY: 0,
            //     format: undefined,
            //     formatter: undefined,
            //     datetimeUTC: true,
            //     datetimeFormatter: {
            //         year: 'yyyy',
            //         month: "MMM 'yy",
            //         day: 'dd MMM',
            //         hour: 'HH:mm',
            //     },
            // },
            labels: {
                offsetY:2,
                offsetX:-4,
                style: {
                    colors:labelColor,
                    // colors: ["blue","orange","grey"],
                    fontSize: '12px',
                    // fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 500,
                    // cssClass: 'apexcharts-xaxis-label',
                },
                formatter: function (val) {
                    if (props.chartSettings?.chart?.labelFormat?.yaxis) {
                        return props.chartSettings.chart.labelFormat.yaxis(i18n, val);
                    }

                    return val === 0 ? '' : val;
                }
            },
        },
        padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }
        //   legend: {
        //     horizontalAlign: 'left'
        //   }
    };


    useEffect(() => {
        if(!chartRef.current) {
            // alert("notready");
        }else{
            // alert("ready");
        }
        // console.log("ref",chartRef.current);
    },[chartRef.current])
    

    const setTickPositions = () => {
        setTimeout(() => {
            if(containerRef.current) {
                var p = containerRef.current.querySelectorAll(".apexcharts-xaxis-texts-g")[0];
                var ticks = containerRef.current.querySelectorAll(".apexcharts-xaxis-tick");
                var lastTickPos = ticks[ticks.length-1]?.getAttribute('x2');
                if(typeof p !== "undefined"){
                    p.childNodes[0]?.setAttribute('x', 10);
                    p.childNodes[p.childNodes.length-1]?.setAttribute('x', lastTickPos-12);
                }
            }
        },[20])
    }
    
    const resetChart = () => {
        if (chartRef.current && chartRef.current.chart) {
            requestAnimationFrame(() => {
                chartRef.current.chart.resetSeries();
            });
            setTickPositions();
        }
    };
      
    // const zoomIn = () => {
    //     if (chartRef.current && chartRef.current.chart) {
    //         requestAnimationFrame(() => {
    //             let { minX, maxX } = chartRef.current.chart.w.globals;
    //             let newMin = minX + (maxX - minX) / 4;
    //             let newMax = maxX - (maxX - minX) / 4;
    //             chartRef.current.chart.zoomX(newMin, newMax);
    //         });
    //         setTickPositions();
    //     }
    // };
      
    // const zoomOut = () => {
    //     if (chartRef.current && chartRef.current.chart) {
    //         requestAnimationFrame(() => {
    //             let { minX, maxX } = chartRef.current.chart.w.globals;
    //             let newMin = minX - (maxX - minX) / 2;
    //             let newMax = maxX + (maxX - minX) / 2;
    //             chartRef.current.chart.zoomX(newMin, newMax);
    //         });
    //         setTickPositions();
    //     }
    // };

    const toggleMode = () => {
        setChartMode(prevMode => prevMode === 'zoom' ? 'pan' : 'zoom');
        setTickPositions();
    };

    useEffect(() => {
        setTickPositions();
    },[chartRef.current,theme.palette.mode]);

    const handleResize = () => {
        setTickPositions();
    }
       
    useEffect(() => {
        window.addEventListener("resize", handleResize);
        
        return () => {
            window.removeEventListener("resize", handleResize);
        }
    },[]);

    const legendRef = useRef(null);
    const itemRefs = useRef([]);
    const measurerRef = useRef(null);

    const [visibleCount, setVisibleCount] = useState(props.values?.length || 0);
    const [measured, setMeasured] = useState(false);
    const [hiddenCount, setHiddenCount] = useState(0);
    const [forceRemeasure, setForceRemeasure] = useState(0);

    // Calculate totals for each series
    const seriesTotals = useMemo(() => {
        if (!props.values) return [];
        return props.values.map(series => {
            const total = series.data.reduce((sum, value) => sum + (value || 0), 0);
            return total;
        });
    }, [props.values]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            setForceRemeasure(prev => prev + 1);
        });
        if (legendRef.current) {
            resizeObserver.observe(legendRef.current);
        }
        return () => resizeObserver.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (!legendRef.current || !props.values) return;

        const containerWidth = legendRef.current.offsetWidth;
        const totalItems = props.values?.length;

        // Use measurer for intrinsic width
        let moreWidth = measurerRef.current ? measurerRef.current.offsetWidth + 16 : 50;

        let usedWidth = 0;
        let count = 0;

        for (let i = 0; i < totalItems; i++) {
            const el = itemRefs.current[i];
            if (!el) continue;
            const width = el.offsetWidth + 16; // gap

            // Check if adding this item + moreWidth (if not last possible) would overflow
            if (usedWidth + width + (i < totalItems - 1 ? moreWidth : 0) > containerWidth) {
                break;
            }

            usedWidth += width;
            count++;
        }

        let hidden = totalItems - count;
        let finalHiddenCount = hidden;

        // If hidden items, recheck with current moreWidth (which may be based on previous hiddenCount, but will converge on re-run)
        if (hidden > 0) {
            // No need for moreRef check, since we use measurer
            moreWidth = measurerRef.current ? measurerRef.current.offsetWidth + 16 : 50;

            // Recheck if the total (visible items + more) overflows
            if (usedWidth + moreWidth > containerWidth) {
                if (count > 0) {
                    const lastItemWidth = itemRefs.current[count - 1].offsetWidth + 16;
                    usedWidth -= lastItemWidth;
                    count--;
                    finalHiddenCount = totalItems - count;
                }
            }
        }

        setVisibleCount(count);
        setHiddenCount(finalHiddenCount);
        setMeasured(true);
    }, [props.values, hiddenCount, forceRemeasure]);

    return (
        
        <Box
            className={cx(props.className, {
                'loading': props.isLoading,
            })} 
            ref={containerRef}
            sx={{
                position:"relative",
                width:1,
                height:1,
                // minHeight:250,
                "& .apexcharts-canvas" : {
                    "& svg" : {
                        marginLeft:props.horizontal ? "-8px" : "-12px",
                        // marginLeft:props.isModal ? "-16px" : "-37px",
                        marginTop:"-16px",
                        position:"absolute",
                        marginRight:"-20px"
                    },
                    // position:"unset",
                    "& .apexcharts-toolbar" : {
                        display:"none",
                    },
                    "& .apexcharts-tooltip, .apexcharts-xaxistooltip, .apexcharts-yaxistooltip" : {
                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.txt["primary"],
                        color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.white[400],
                        border:"none",
                        "& .apexcharts-tooltip-title" : {
                            display:"none"
                        }
                    },
                },
                "&.loading .sh-area-chart" : {
                    animation: 'pulse 2.5s infinite'
                },
                "@media screen and (max-width: 1024px)" : {
                    display:"flex",
                    flexDirection:"column"
                },
                "@media screen and (max-width: 47.9375em)" : {
                    minHeight:"200px",
                    // paddingRight:3,
                }
            }}
        >
            {!props.isLoading && !props.isError && !allValuesAreZero ?
                <Box
                    className={cx(undefined,{
                        'hide-mobile': props.values?.length <= 1
                    })}
                    sx={{
                        position:"absolute",
                        right:24,
                        top:"-40px",
                        height:40,
                        display:"inline-flex",
                        alignItems:"center",
                        background:colors.grey[200],
                        // background: chartTokens[props.color][colorType][100],
                        padding:0.5,
                        borderRadius:5,
                        gap:0.5,
                        zIndex:120,

                        "@media screen and (max-width: 1024px)" : {
                            position:'static',
                            order:2,
                            marginTop: "-24px",
                            mr:3,
                            pr:1,
                        },
                        "@media screen and (max-width: 768px)" : {
                            position:'static',
                            mr:3,
                            "&.hide-mobile": {
                                display:"none",
                            }
                        },
                        ...props.sx?.legend
                        // "@media screen and (max-width: 47.9375em)" : {
                        //     position:"relative",
                        //     display: props.isModal ? "flex" : "none",
                        //     top:"unset",
                        //     width:"calc(100% - 24px)",
                        //     // marginRight:3,
                        //     marginTop:2,
                        //     marginLeft:3
                        // }
                    }}
                >
                    {props.values?.length > 0 && (
                        <Box
                            ref={legendRef}
                            sx={{
                                height: 32,
                                px: 2,
                                pr: 0.5,
                                background: colors.grey[100],
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent:'center',
                                gap: 2,
                                width: 400,
                                overflow: "hidden", // important

                                "@media screen and (max-width: 1024px)": {
                                    flex: 1,
                                    mr: 0.5,
                                },
                                "@media screen and (max-width: 768px)" : {
                                    m:0,
                                    px:0.5,
                                    pl:2,
                                },
                                ...props.sx?.legend?.inner
                            }}
                        >
                            {props.values
                                ?.slice(0, measured ? visibleCount : props.values?.length)
                                .map((series, index) => (
                                    <Box
                                        key={index}
                                        ref={(el) => (itemRefs.current[index] = el)}
                                        title={props.smallLegend ? t(series.name) : undefined}
                                        sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        whiteSpace: "nowrap",
                                        }}
                                    >
                                        <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            backgroundColor: props.staticColors[index],
                                        }}
                                        />
                                        {!props.smallLegend && (
                                            <Text fontSize={12} noBreak>
                                                {t(series.name)}
                                            </Text>
                                        )}
                                        {!props.smallLegend && (
                                            <Text fontSize={12} noBreak bold>
                                                {seriesTotals[index]?.toLocaleString() ?? 0}
                                            </Text>
                                        )}
                                    </Box>
                                ))
                            }

                            {measured && hiddenCount > 0 && (
                                <ChartLegendPopover
                                    hiddenCount={hiddenCount}
                                    values={props.values}
                                    staticColors={props.staticColors}
                                    seriesTotals={seriesTotals}
                                />
                            )}

                            {/* Offscreen measurer for intrinsic more width */}
                            <Box
                                ref={measurerRef}
                                sx={{
                                    position: 'absolute',
                                    visibility: 'hidden',
                                    fontSize: 12,
                                    px: 1,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                +{hiddenCount} {t("more")}
                            </Box>
                        </Box>
                    )}


                    {/* <ButtonBase 
                        sx={{
                            height:32,
                            width:32,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            borderRadius:4,
                            // background: chartTokens[props.color][colorType][1000],
                            color: colors.txt["primary"],
                            "@media screen and (max-width: 47.9375em)" : {
                                flex:1,
                                width:"unset"
                            },
                            "@media screen and (max-width: 47.9375em)" : {
                                display:"none"
                            }
                        }}
                    
                        onClick={resetChart}
                    >
                        <HomeIcon fontSize="small"/>
                    </ButtonBase>
                    

                    <Box
                        sx={{
                            height:32,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            padding:0.5,
                            // background: chartTokens[props.color][colorType][300],
                            borderRadius:4,
                            gap:0.5,
                            "@media screen and (max-width: 47.9375em)" : {
                                display:"none"
                            }
                        }}
                    >
                        <ButtonBase 
                            sx={{
                                height:24,
                                width:24,
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                                borderRadius:4,
                                // color: chartMode === "zoom" ? "#fff" : chartTokens.text.primary,
                                // background: chartMode === "zoom" ? chartTokens[props.color][colorType][1000] : null,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1,
                                    width:"unset"
                                }
                            }}
                        
                            onClick={toggleMode}
                        >
                            <SearchIcon fontSize="small"/>
                        </ButtonBase>
                        <ButtonBase 
                            sx={{
                                height:24,
                                width:24,
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                                borderRadius:4,
                                // color: chartMode === "pan" ? "#fff" : chartTokens.text.primary,
                                // background: chartMode === "pan" ? chartTokens[props.color][colorType][1000] : null,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1,
                                    width:"unset"
                                }
                            }}
                        
                            onClick={toggleMode}
                        >
                            <PanToolAltIcon fontSize="small"/>
                        </ButtonBase>

                    </Box> */}
                </Box>
            :null}


            <AreaContent
                key={chartMode}
                // ref={chartRef}
                type="area"
                series={series}
                options={options}
                height={props.isModal ? "100%" : typeof props.height !== "undefined" ? props.height : "300px"}

            />

            {allValuesAreZero && <NoChartData sx={{left:0,}} />}
            {props.isError && <ChartError sx={{left:0,borderRadius:4,}} onRetry={props.onRetry}/>}
                
            
        </Box>
    )
}

export const AreaChartContent = (props) => {
    if(typeof props.colors === "undefined" && typeof props.staticColors === "undefined") throw new Error("Colors not defined");
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {i18n} = useTranslation();
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [chartMode, setChartMode] = useState('zoom'); 
    const colorType = theme.palette.mode === "dark" ? "light" : "st"; 
    const {t} = useTranslation();

    const getColor = () => {
        if(typeof props.color === "undefined") return colors.txt["secondary"];
        if(props.color !== "default") return chartTokens.text.primary;
        return colors.txt["secondary"];
    }

    const getButtonTextColor = () => {
        if(props.color === "default" && theme.palette.mode === "dark") {
            return colors.txt.primary;
        }
        return chartTokens.text.primary;
    }

    const randomData = Array.from({ length: 7 }, () => Math.floor(Math.random() * 60) + 40);
   
    // Define the initial values based on the isLoading prop
    const values = props.isLoading || props.isError ? [{
        name: 'Loading',
        data: randomData
    }] : props.values

    const allValuesAreZero = useMemo(() => {
        return values.every(item => item.data.every(value => value === 0 || isNaN(value)));
    }, [values]);

    // Deep clone series to prevent ApexCharts from mutating frozen objects (e.g., from Redux/RTK Query)
    const series = useMemo(() => {
        // Builder preview only shows a tinted placeholder — reuse the one static dataset instead of
        // recomputing/cloning the real series each render.
        if (props.preview) return PREVIEW_SERIES;
        const rawSeries = !allValuesAreZero
            ? (props.useTranslation
                ? values.map((value) => ({
                    name: t(value.name.toLowerCase()),
                    data: value.data
                }))
                : values)
            : [{name: "Data", data: randomData}];
        return JSON.parse(JSON.stringify(rawSeries));
    }, [props.preview, allValuesAreZero, values, props.useTranslation, t, randomData]);

    const getChartColors = () => {
        if (props.isLoading || allValuesAreZero) {
            // A dashboard-builder preview tints the placeholder in the flat preview color; otherwise plain grey.
            return [props.preview ? colors.purple[400] : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100])];
        }
        if(props.isError) {
            return [colors.red[200]]
        }

        //Static colors take priority
        if(typeof props.staticColors !== "undefined") return props.staticColors;


        let myColors = [];

        var keys = Object.keys(chartTokens[props.colors[0]][colorType]);
        for (var i = keys.length - 1; i >= keys.length - values.length; i--) {
            myColors.push(chartTokens[props.colors[0]][colorType][keys[i]]);
        }
        return myColors;

    }

    // Memoize tooltip formatter to prevent recreation on every render
    const tooltipFormatter = useCallback(({ series, dataPointIndex, w }) => {
        // Get the actual category from props.categories instead of w.globals.labels
        let category = props.categories?.[dataPointIndex] ?? dataPointIndex;

        // Apply xaxis formatter if it exists
        if (props.chartSettings?.chart?.labelFormat?.xaxis) {
            category = props.chartSettings.chart.labelFormat.xaxis(i18n, category);
        }

        const formatterFn = props.chartSettings?.chart?.labelFormat?.dataLabels;

        let tooltipHtml = `
            <div class="apex-tooltip-custom" style="padding:8px 12px;">
                <div style="margin-bottom:6px;font-weight:600;">
                ${category}
                </div>
        `;

        w.globals.seriesNames.forEach((name, i) => {
            let val = series[i][dataPointIndex];

            // Hide exact zero
            if (val === 0) val = 0;

            // Hide values that would round to 0 (e.g. 0.001 → €0.00)
            if (Math.round(val * 100) / 100 === 0) val = 0;

            if (typeof formatterFn === 'function') {
                val = formatterFn(i18n, val);
            }

            if (val !== undefined) {
                tooltipHtml += `
                    <div style="display:flex;align-items:center;margin:2px 0;">
                        <span
                            style="
                                width:10px;
                                height:10px;
                                background:${w.globals.colors[i]};
                                display:inline-block;
                                margin-right:6px;
                                border-radius:5px;
                            "
                        >
                        </span>
                        <span>${t(name)}: <b>${val}</b></span>
                    </div>
                `;
            }
        });

        tooltipHtml += `</div>`;
        return tooltipHtml;
    }, [props.chartSettings?.chart?.labelFormat?.dataLabels, props.chartSettings?.chart?.labelFormat?.xaxis, props.categories, i18n, t]);

    const options = {
        chart: {
            selection: {
              enabled: true
            },
            pan: {
                enabled:true,
            },
            stacked: props.stacked ?? true,
            // zoom: {
            //   enabled: chartMode === 'zoom',
            // },
            // pan: {
            //   enabled: chartMode === 'pan',
            // },
            // zoom: {

            //     enabled: true,
            //     type: 'x',
            //     autoScaleYaxis: false,
            // },
            toolbar: {
                autoSelected: chartMode,
                tools: {
                  download: true,
                  selection: true,
                  zoom: true,
                  zoomin: true,
                  zoomout: true,
                  pan: true
                }
            },
            // autoSelected: 'selection',
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            colors: undefined,
            width:0
        },
        tooltip: {
            shared: window.innerWidth >= 768,
            intersect: window.innerWidth < 768,
            followCursor: true,
            custom: tooltipFormatter
        },
        colors:getChartColors(),
        fill: {
            type: 'gradient',
            gradient: {
                // shade: "light",
                type: "vertical",
                shadeIntensity: 0,
                opacityFrom: 1,
                opacityTo: 1 
            }
        },
        // fill: {
        //     type: "gradient",
        //     gradient: {
        //       shadeIntensity: 1,
        //       opacityFrom: 1,
        //       opacityTo: 1,
        //       stops: [100, 1]
        //     }
        // },
        legend:{
            show:false,
        },
        grid:{
            show:false,
        },
        xaxis: {
            categories: props.categories ?? [1991, 1992, 1993, 1994, 1995, 1996, 1997, 1998, 1999],
            tickAmount: props.tickAmount ?? 5,
            // type: 'category',
            // categories: [],
            // tickAmount: undefined,
            // tickPlacement: 'between',
            // min: undefined,
            // max: undefined,
            // range: undefined,
            // floating: false,
            // decimalsInFloat: undefined,
            // overwriteCategories: undefined,
            // position: 'bottom',
            labels: {
                // show: true,
                // rotate: -45,
                // rotateAlways: false,
                // hideOverlappingLabels: true,
                // showDuplicates: false,
                // trim: false,
                // minHeight: undefined,
                // maxHeight: 120,
                style: {
                    colors:getColor(),
                    // colors: ["blue","orange","grey"],
                    fontSize: '12px',
                    // fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 500,
                    
                    cssClass: 'apexcharts-xaxis-label',
                },
                offsetX: 0,
            },
            // group: {
            //   groups: [],
            //   style: {
            //     colors: [],
            //     fontSize: '12px',
            //     fontWeight: 400,
            //     fontFamily: undefined,
            //     cssClass: ''
            //   }
            // },
            axisBorder: {
                show: false,
                // color: 'orange',
                // height: 0,
                // width: '100%',
                // offsetX: 0,
                // offsetY: 0
            },
            // axisTicks: {
            //     show: true,
            //     borderType: 'solid',
            //     color: '#78909C',
            //     height: 6,
            //     offsetX: 0,
            //     offsetY: 0
            // },
           
            // title: {
            //     text: undefined,
            // },
            // crosshairs: {
            //     show: true,
            //     width: 1,
            //     position: 'back',
            //     opacity: 0.9,        
            //     stroke: {
            //         color: '#b6b6b6',
            //         width: 0,
            //         dashArray: 0,
            //     },
            //     fill: {
            //         type: 'solid',
            //         color: '#B1B9C4',
            //         gradient: {
            //             colorFrom: '#D8E3F0',
            //             colorTo: '#BED1E6',
            //             stops: [0, 100],
            //             opacityFrom: 0.4,
            //             opacityTo: 0.5,
            //         },
            //     },
            //     dropShadow: {
            //         enabled: false,
            //         top: 0,
            //         left: 0,
            //         blur: 1,
            //         opacity: 0.4,
            //     },
            // },
        },
        yaxis: {
            min: props.min ?? 0,  // Set the minimum value for the Y-axis
            show: props.showYAxis ?? false,
            // opposite: true
            labels: {
                style: {
                    colors:getColor(),
                    // colors: ["blue","orange","grey"],
                    fontSize: '12px',
                    // fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 500,
                    // cssClass: 'apexcharts-xaxis-label',
                },
                offsetX: 0,
                offsetY: 0,
                format: undefined,
                formatter: undefined,
                datetimeUTC: true,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: "MMM 'yy",
                    day: 'dd MMM',
                    hour: 'HH:mm',
                },
            },
        },
        padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }
        //   legend: {
        //     horizontalAlign: 'left'
        //   }
    };


    useEffect(() => {
        if(!chartRef.current) {
            // alert("notready");
        }else{
            // alert("ready");
        }
        // console.log("ref",chartRef.current);
    },[chartRef.current])
    

    const setTickPositions = () => {
        setTimeout(() => {
            if(containerRef.current) {
                var p = containerRef.current.querySelectorAll(".apexcharts-xaxis-texts-g")[0];
                var ticks = containerRef.current.querySelectorAll(".apexcharts-xaxis-tick");
                var lastTickPos = ticks[ticks.length-1]?.getAttribute('x2');
                if(typeof p !== "undefined"){
                    p.childNodes[0]?.setAttribute('x', 10);
                    p.childNodes[p.childNodes.length-1]?.setAttribute('x', lastTickPos-12);
                }
            }
        },[20])
    }
    
    const resetChart = () => {
        if (chartRef.current && chartRef.current.chart) {
            requestAnimationFrame(() => {
                chartRef.current.chart.resetSeries();
            });
            setTickPositions();
        }
    };
      
    const zoomIn = () => {
        if (chartRef.current && chartRef.current.chart) {
            requestAnimationFrame(() => {
                let { minX, maxX } = chartRef.current.chart.w.globals;
                let newMin = minX + (maxX - minX) / 4;
                let newMax = maxX - (maxX - minX) / 4;
                chartRef.current.chart.zoomX(newMin, newMax);
            });
            setTickPositions();
        }
    };
      
    const zoomOut = () => {
        if (chartRef.current && chartRef.current.chart) {
            requestAnimationFrame(() => {
                let { minX, maxX } = chartRef.current.chart.w.globals;
                let newMin = minX - (maxX - minX) / 2;
                let newMax = maxX + (maxX - minX) / 2;
                chartRef.current.chart.zoomX(newMin, newMax);
            });
            setTickPositions();
        }
    };

    const toggleMode = () => {
        setChartMode(prevMode => prevMode === 'zoom' ? 'pan' : 'zoom');
        setTickPositions();
    };

    useEffect(() => {
        setTickPositions();
    },[chartRef.current,theme.palette.mode]);

    const handleResize = () => {
        setTickPositions();
    }
       
    useEffect(() => {
        window.addEventListener("resize", handleResize);
        
        return () => {
            window.removeEventListener("resize", handleResize);
        }
    },[]);
  

    return (
        
        <Box
            className={cx(props.className, {
                'loading': props.isLoading,
            })} 
            ref={containerRef}
            sx={{
                position:"relative",
                width:1,
                height:1,
                // minHeight:250,
                "& .apexcharts-canvas" : {
                    "& svg" : {
                        marginLeft:props.horizontal ? "-8px" : "-12px",
                        // marginLeft:props.isModal ? "-16px" : "-37px",
                        marginTop:"-16px",
                        position:"absolute",
                        marginRight:"-20px"
                    },
                    // position:"unset",
                    "& .apexcharts-toolbar" : {
                        display:"none",
                    },
                    "& .apexcharts-tooltip, .apexcharts-xaxistooltip, .apexcharts-yaxistooltip" : {
                        background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                        color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.white[400],
                        border:"none",
                        "& .apexcharts-tooltip-title" : {
                            display:"none"
                        }
                    },
                },
                "&.loading .sh-area-chart" : {
                    animation: 'pulse 2.5s infinite'
                },
                "@media screen and (max-width: 47.9375em)" : {
                    minHeight:"200px",
                    // paddingRight:3,
                }
            }}
        >
            {props.optionsEnabled && !props.isLoading && !props.isError && !allValuesAreZero ?
                <Box
                    sx={{
                        position:"absolute",
                        right:24,
                        top:"-40px",
                        height:40,
                        display:"inline-flex",
                        alignItems:"center",
                        background: chartTokens[props.color][colorType][100],
                        padding:0.5,
                        borderRadius:5,
                        gap:0.5,
                        zIndex:120,
                        "@media screen and (max-width: 47.9375em)" : {
                            position:"relative",
                            display: props.isModal ? "flex" : "none",
                            top:"unset",
                            width:"calc(100% - 24px)",
                            // marginRight:3,
                            marginTop:2,
                            marginLeft:3
                        }
                    }}
                >
                    <ButtonBase 
                        sx={{
                            height:32,
                            width:32,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            borderRadius:4,
                            background: chartTokens[props.color][colorType][1000],
                            color: "#fff",
                            "@media screen and (max-width: 47.9375em)" : {
                                flex:1,
                                width:"unset"
                            }
                        }}
                    
                        onClick={resetChart}
                    >
                        <HomeIcon fontSize="small"/>
                    </ButtonBase>
                    <ButtonBase 
                        sx={{
                            height:32,
                            width:32,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            borderRadius:4,
                            color: getButtonTextColor(),
                            "@media screen and (max-width: 47.9375em)" : {
                                flex:1,
                                width:"unset"
                            }
                        }}
                    
                        onClick={zoomIn}
                    >
                        <ZoomInMapOutlinedIcon fontSize="medium"/>
                    </ButtonBase>
                    <ButtonBase 
                        sx={{
                            height:32,
                            width:32,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            borderRadius:4,
                            color: getButtonTextColor(),
                            "@media screen and (max-width: 47.9375em)" : {
                                flex:1,
                                width:"unset"
                            }
                        }}
                    
                        onClick={zoomOut}
                    >
                        <ZoomOutMapOutlinedIcon fontSize="medium"/>
                    </ButtonBase>

                    {/* Switch */}
                    <Box
                        sx={{
                            height:32,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            padding:0.5,
                            background: chartTokens[props.color][colorType][300],
                            borderRadius:4,
                            gap:0.5,
                            "@media screen and (max-width: 47.9375em)" : {
                                display:"none"
                            }
                        }}
                    >
                        <ButtonBase 
                            sx={{
                                height:24,
                                width:24,
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                                borderRadius:4,
                                color: chartMode === "zoom" ? "#fff" : chartTokens.text.primary,
                                background: chartMode === "zoom" ? chartTokens[props.color][colorType][1000] : null,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1,
                                    width:"unset"
                                }
                            }}
                        
                            onClick={toggleMode}
                        >
                            <SearchIcon fontSize="small"/>
                        </ButtonBase>
                        <ButtonBase 
                            sx={{
                                height:24,
                                width:24,
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                                borderRadius:4,
                                color: chartMode === "pan" ? "#fff" : chartTokens.text.primary,
                                background: chartMode === "pan" ? chartTokens[props.color][colorType][1000] : null,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1,
                                    width:"unset"
                                }
                            }}
                        
                            onClick={toggleMode}
                        >
                            <PanToolAltIcon fontSize="small"/>
                        </ButtonBase>

                    </Box>
                </Box>
            :null}


            <AreaContent
                key={chartMode}
                // ref={chartRef}
                type="area"
                series={series}
                options={options}
                height={props.isModal ? "100%" : typeof props.height !== "undefined" ? props.height : "300px"}

            />

            {allValuesAreZero && <NoChartData type="area"/>}
            {props.isError && <ChartError type="area" onRetry={props.onRetry}/>}
            
        </Box>
    )
}

const AreaChart = (props) => {
    return (
        <ShChart
            disallowModal={props.disallowModal ?? false}
        >
            <ChartCard 
                optionsEnabled={props.optionsEnabled ?? true}
                {...props}
                type={"area"}
            >
                <AreaChartContent
                    {...props}
                    optionsEnabled={props.optionsEnabled ?? true}
                    color={props.color} 
                    values={props.values} 
                    categories={props.categories} 
                    tickAmount={props.tickAmount ?? 30}/>
            </ChartCard>
        </ShChart>
    );
}
export default AreaChart;